import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiSearch,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { toast } from "react-toastify";
import spApi from "store/services/sp.services";

const FILTER_TABS = [
  { id: "all", label: "All" },
  { id: "unmapped", label: "Unmapped" },
  { id: "mapped", label: "Mapped" },
  { id: "auto", label: "Auto-matched" },
];

const matchesSearch = (value, query) =>
  (value ?? "").toString().toLowerCase().includes(query);

const buildLearnerIndex = (unmappedWyzed, workers) => {
  const index = new Map();
  unmappedWyzed.forEach((learner) => {
    index.set(learner.wyzed_user_id, learner);
  });
  workers.forEach((row) => {
    if (row.wyzed_user_id && !index.has(row.wyzed_user_id)) {
      index.set(row.wyzed_user_id, {
        wyzed_user_id: row.wyzed_user_id,
        name: row.wyzed_name,
        email: row.wyzed_email,
      });
    }
  });
  return index;
};

const SearchableLearnerPicker = ({
  value,
  options,
  onChange,
  placeholder = "Select Wyzed learner…",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const selected = options.find((o) => o.wyzed_user_id === value) ?? null;

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        matchesSearch(o.name, q) ||
        matchesSearch(o.email, q) ||
        matchesSearch(o.wyzed_user_id, q)
    );
  }, [options, query]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (learnerId) => {
    onChange(learnerId || null);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative min-w-[240px]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
          open
            ? "border-blue-500 ring-1 ring-blue-500"
            : "border-gray-300 hover:border-gray-400"
        } ${selected ? "bg-white text-gray-900" : "bg-gray-50 text-gray-500"}`}
      >
        <span className="truncate">
          {selected ? (
            <>
              <span className="font-medium">{selected.name || "Unknown"}</span>
              {selected.email && (
                <span className="text-gray-500"> · {selected.email}</span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <FiChevronDown
          className={`flex-shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[280px] rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Wyzed name or email…"
                className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => handleSelect("")}
                className={`flex w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                  !value ? "bg-blue-50 text-blue-700" : "text-gray-500"
                }`}
              >
                Not mapped
              </button>
            </li>
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-gray-400">
                No learners match your search
              </li>
            ) : (
              filteredOptions.map((learner) => (
                <li key={learner.wyzed_user_id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(learner.wyzed_user_id)}
                    className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                      value === learner.wyzed_user_id
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-900"
                    }`}
                  >
                    <span className="font-medium truncate">{learner.name || "Unknown"}</span>
                    {learner.email && (
                      <span className="truncate text-xs text-gray-500">{learner.email}</span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const WyzedWorkerMapping = ({ onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkCreateAllOpen, setBulkCreateAllOpen] = useState(false);
  const [bulkCreateSelectedOpen, setBulkCreateSelectedOpen] = useState(false);
  const [selectedCreateIds, setSelectedCreateIds] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [unmappedWyzed, setUnmappedWyzed] = useState([]);
  const [vertexSearch, setVertexSearch] = useState("");
  const [wyzedSearch, setWyzedSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const loadPreview = async () => {
    try {
      setLoading(true);
      const response = await spApi.getWyzedWorkerMappingPreview();
      setWorkers(response?.data?.workers ?? []);
      setUnmappedWyzed(response?.data?.unmapped_wyzed_learners ?? []);
    } catch (err) {
      toast.error(err?.message ?? "Failed to load worker mappings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, []);

  const learnerIndex = useMemo(
    () => buildLearnerIndex(unmappedWyzed, workers),
    [unmappedWyzed, workers]
  );

  const assignedIds = useMemo(
    () => new Set(workers.filter((row) => row.wyzed_user_id).map((row) => row.wyzed_user_id)),
    [workers]
  );

  const stats = useMemo(() => {
    const mapped = workers.filter((row) => row.wyzed_user_id).length;
    const auto = workers.filter((row) => row.auto_matched && row.wyzed_user_id).length;
    const suggested = workers.filter(
      (row) => !row.wyzed_user_id && row.suggested_wyzed_user_id
    ).length;
    return {
      total: workers.length,
      mapped,
      unmapped: workers.length - mapped,
      auto,
      suggested,
      wyzedAvailable: unmappedWyzed.length,
    };
  }, [workers, unmappedWyzed]);

  const wyzedEmailsInUse = useMemo(() => {
    const emails = new Set();
    // Only unmapped Wyzed learners (and suggested-but-unassigned) count as
    // available email matches. Emails of already-mapped learners must not
    // prompt remapping to a different Vertex worker.
    unmappedWyzed.forEach((learner) => {
      const email = (learner.email || "").trim().toLowerCase();
      if (email) emails.add(email);
    });
    workers.forEach((row) => {
      if (row.wyzed_user_id) return;
      const email = (row.suggested_wyzed_email || "").trim().toLowerCase();
      if (email) emails.add(email);
    });
    return emails;
  }, [unmappedWyzed, workers]);

  const isCreatableOnWyzed = useCallback(
    (row) => {
      if (row.wyzed_user_id) return false;
      const email = (row.worker_email || "").trim();
      if (!email) return false;
      return !wyzedEmailsInUse.has(email.toLowerCase());
    },
    [wyzedEmailsInUse]
  );

  const creatableWorkers = useMemo(
    () => workers.filter(isCreatableOnWyzed),
    [workers, isCreatableOnWyzed]
  );

  const getOptionsForRow = (row) => {
    const options = [];
    assignedIds.forEach((id) => {
      if (id !== row.wyzed_user_id) return;
      const learner = learnerIndex.get(id);
      if (learner) options.push(learner);
    });
    unmappedWyzed.forEach((learner) => {
      if (!assignedIds.has(learner.wyzed_user_id)) {
        options.push(learner);
      }
    });
    if (row.wyzed_user_id && !options.some((o) => o.wyzed_user_id === row.wyzed_user_id)) {
      const learner = learnerIndex.get(row.wyzed_user_id);
      if (learner) options.unshift(learner);
    }
    return options.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  const filteredWorkers = useMemo(() => {
    const vertexQ = vertexSearch.trim().toLowerCase();
    const wyzedQ = wyzedSearch.trim().toLowerCase();

    return workers.filter((row) => {
      if (filter === "unmapped" && row.wyzed_user_id) return false;
      if (filter === "mapped" && !row.wyzed_user_id) return false;
      if (filter === "auto" && !(row.auto_matched && row.wyzed_user_id)) return false;

      if (vertexQ) {
        const vertexMatch =
          matchesSearch(row.worker_name, vertexQ) ||
          matchesSearch(row.worker_email, vertexQ);
        if (!vertexMatch) return false;
      }

      if (wyzedQ) {
        const wyzedMatch =
          matchesSearch(row.wyzed_name, wyzedQ) ||
          matchesSearch(row.wyzed_email, wyzedQ) ||
          matchesSearch(row.wyzed_user_id, wyzedQ);
        if (!wyzedMatch) return false;
      }

      return true;
    });
  }, [workers, vertexSearch, wyzedSearch, filter]);

  const creatableFilteredWorkers = useMemo(
    () => filteredWorkers.filter(isCreatableOnWyzed),
    [filteredWorkers, isCreatableOnWyzed]
  );

  const selectedCreateSet = useMemo(
    () => new Set(selectedCreateIds.map(String)),
    [selectedCreateIds]
  );

  const allFilteredCreatableSelected =
    creatableFilteredWorkers.length > 0 &&
    creatableFilteredWorkers.every((row) =>
      selectedCreateSet.has(String(row.employee_profile_id))
    );

  const toggleSelectWorker = (employeeProfileId) => {
    const id = String(employeeProfileId);
    setSelectedCreateIds((prev) =>
      prev.map(String).includes(id)
        ? prev.filter((item) => String(item) !== id)
        : [...prev, employeeProfileId]
    );
  };

  const toggleSelectAllFilteredCreatable = () => {
    if (allFilteredCreatableSelected) {
      const filteredIds = new Set(
        creatableFilteredWorkers.map((row) => String(row.employee_profile_id))
      );
      setSelectedCreateIds((prev) =>
        prev.filter((id) => !filteredIds.has(String(id)))
      );
    } else {
      const ids = new Set(selectedCreateIds.map(String));
      creatableFilteredWorkers.forEach((row) => {
        ids.add(String(row.employee_profile_id));
      });
      setSelectedCreateIds(
        workers
          .filter((row) => ids.has(String(row.employee_profile_id)))
          .map((row) => row.employee_profile_id)
      );
    }
  };

  const showBulkCreateResult = (result) => {
    const created = result?.created_count ?? 0;
    const skipped = result?.skipped_count ?? 0;
    const failed = result?.failed_count ?? 0;

    if (created > 0) {
      toast.success(
        `Created ${created} worker${created === 1 ? "" : "s"} in Wyzed.` +
          (skipped > 0 ? ` ${skipped} skipped.` : "") +
          (failed > 0 ? ` ${failed} failed.` : "")
      );
    } else if (failed > 0) {
      toast.error(`Failed to create ${failed} worker${failed === 1 ? "" : "s"} in Wyzed.`);
    } else {
      toast.info("No workers were created in Wyzed.");
    }

    if (failed > 0 && result?.failed?.[0]?.message) {
      toast.error(result.failed[0].message, { autoClose: 8000 });
    }
  };

  const selectedCreatableWorkers = useMemo(
    () =>
      workers.filter((row) =>
        selectedCreateSet.has(String(row.employee_profile_id))
      ),
    [workers, selectedCreateSet]
  );

  const renderWorkerPreviewList = (rows, max = 5) => {
    if (rows.length === 0) return null;
    const shown = rows.slice(0, max);
    const remaining = rows.length - shown.length;

    return (
      <ul className="mt-3 max-h-36 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        {shown.map((row) => (
          <li key={row.employee_profile_id} className="truncate">
            <span className="font-medium text-slate-900">{row.worker_name}</span>
            {row.worker_email ? (
              <span className="text-slate-500"> · {row.worker_email}</span>
            ) : null}
          </li>
        ))}
        {remaining > 0 && (
          <li className="text-xs text-slate-500">…and {remaining} more</li>
        )}
      </ul>
    );
  };

  const BulkCreateConfirmBody = ({ count, workersPreview }) => (
    <div className="space-y-4 text-sm text-slate-600">
      <p>
        You are about to create{" "}
        <span className="font-semibold text-slate-900">
          {count} new learner{count === 1 ? "" : "s"}
        </span>{" "}
        in your connected Wyzed account. Each selected Vertex360 worker who is not yet
        linked will get a new Wyzed learner profile using their name and email from
        Vertex360.
      </p>
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-950">
        <p className="font-medium">Please confirm you want to proceed</p>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-relaxed">
          <li>New users will be created in Wyzed (not just linked to existing learners).</li>
          <li>Vertex360 will automatically link each worker after creation.</li>
          <li>Workers without an email address will be skipped.</li>
          <li>Workers who already have a matching Wyzed learner by email will be skipped.</li>
        </ul>
      </div>
      {workersPreview && renderWorkerPreviewList(workersPreview)}
    </div>
  );

  const handleBulkCreateSelected = async () => {
    if (selectedCreateIds.length === 0) {
      toast.error("Select at least one worker to create in Wyzed.");
      return;
    }

    try {
      setBulkCreating(true);
      const response = await spApi.bulkCreateWyzedWorkers({
        employee_profile_ids: selectedCreateIds,
      });
      showBulkCreateResult(response?.data);
      setSelectedCreateIds([]);
      setBulkCreateSelectedOpen(false);
      onSaved?.();
      await loadPreview();
    } catch (err) {
      toast.error(err?.message ?? "Failed to create workers in Wyzed.");
    } finally {
      setBulkCreating(false);
    }
  };

  const handleBulkCreateAll = async () => {
    try {
      setBulkCreating(true);
      const response = await spApi.bulkCreateWyzedWorkers({ all_unmapped: true });
      showBulkCreateResult(response?.data);
      setBulkCreateAllOpen(false);
      setSelectedCreateIds([]);
      onSaved?.();
      await loadPreview();
    } catch (err) {
      toast.error(err?.message ?? "Failed to create workers in Wyzed.");
    } finally {
      setBulkCreating(false);
    }
  };

  const handleMappingChange = (employeeProfileId, wyzedUserId) => {
    const learner = wyzedUserId ? learnerIndex.get(wyzedUserId) : null;
    setWorkers((prev) =>
      prev.map((row) => {
        if (row.employee_profile_id !== employeeProfileId) return row;

        const workerEmail = (row.worker_email || "").trim().toLowerCase();
        const learnerEmail = (learner?.email || "").trim().toLowerCase();
        const emailMatched =
          Boolean(wyzedUserId) &&
          workerEmail !== "" &&
          learnerEmail !== "" &&
          workerEmail === learnerEmail;

        return {
          ...row,
          wyzed_user_id: wyzedUserId || null,
          wyzed_name: learner?.name ?? null,
          wyzed_email: learner?.email ?? null,
          auto_matched: emailMatched,
        };
      })
    );
  };

  const applyEmailMatches = () => {
    const usedIds = new Set(
      workers.filter((row) => row.wyzed_user_id).map((row) => row.wyzed_user_id)
    );
    let matched = 0;

    setWorkers((prev) =>
      prev.map((row) => {
        // Never override an existing saved mapping with an email suggestion.
        if (row.wyzed_user_id) return row;

        // Prefer server-side suggestion (already excludes learners mapped elsewhere).
        if (
          row.suggested_wyzed_user_id &&
          !usedIds.has(row.suggested_wyzed_user_id)
        ) {
          usedIds.add(row.suggested_wyzed_user_id);
          matched += 1;
          return {
            ...row,
            wyzed_user_id: row.suggested_wyzed_user_id,
            wyzed_name: row.suggested_wyzed_name,
            wyzed_email: row.suggested_wyzed_email,
            auto_matched: true,
          };
        }

        const email = (row.worker_email || "").trim().toLowerCase();
        if (!email) return row;

        const match = unmappedWyzed.find(
          (learner) =>
            !usedIds.has(learner.wyzed_user_id) &&
            (learner.email || "").trim().toLowerCase() === email
        );
        if (!match) return row;

        usedIds.add(match.wyzed_user_id);
        matched += 1;
        return {
          ...row,
          wyzed_user_id: match.wyzed_user_id,
          wyzed_name: match.name,
          wyzed_email: match.email,
          auto_matched: true,
        };
      })
    );

    if (matched > 0) {
      toast.success(`Matched ${matched} worker${matched === 1 ? "" : "s"} by email.`);
    } else {
      toast.info("No new email matches found for unmapped workers.");
    }
  };

  const clearFilters = () => {
    setVertexSearch("");
    setWyzedSearch("");
    setFilter("all");
  };

  const hasActiveFilters =
    vertexSearch.trim() || wyzedSearch.trim() || filter !== "all";

  const handleSave = async () => {
    try {
      setSaving(true);
      await spApi.saveWyzedWorkerMappings({
        mappings: workers.map((row) => ({
          employee_profile_id: row.employee_profile_id,
          wyzed_user_id: row.wyzed_user_id,
          wyzed_email: row.wyzed_email,
          auto_matched: row.auto_matched,
        })),
      });
      toast.success("Worker mappings saved.");
      onSaved?.();
      loadPreview();
    } catch (err) {
      toast.error(err?.message ?? "Failed to save mappings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <CircularLoader classes="flex justify-center" classloader="loader-l" />
      </div>
    );
  }

  return (
    <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-5 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Worker Mapping</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Match Vertex360 workers to Wyzed learners, or create missing workers in
              Wyzed in bulk. Use search and filters to work through large lists quickly.
            </p>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <CustomButton
              label={`Create selected (${selectedCreateIds.length})`}
              variant="secondary"
              showLoading={bulkCreating}
              isDisabled={bulkCreating || saving || selectedCreateIds.length === 0}
              clickHandler={() => setBulkCreateSelectedOpen(true)}
            />
            <CustomButton
              label={`Create all missing (${creatableWorkers.length})`}
              variant="secondary"
              showLoading={bulkCreating}
              isDisabled={bulkCreating || saving || creatableWorkers.length === 0}
              clickHandler={() => setBulkCreateAllOpen(true)}
            />
            <CustomButton
              label="Auto-match by email"
              variant="secondary"
              isDisabled={bulkCreating || saving}
              clickHandler={applyEmailMatches}
            />
            <CustomButton
              label="Refresh"
              variant="secondary"
              isDisabled={bulkCreating || saving}
              clickHandler={loadPreview}
            />
            <CustomButton
              label="Save Mappings"
              showLoading={saving}
              isDisabled={saving || bulkCreating}
              clickHandler={handleSave}
            />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Vertex workers", value: stats.total, icon: FiUsers },
            { label: "Mapped", value: stats.mapped, icon: FiCheck },
            { label: "Unmapped", value: stats.unmapped, icon: FiUser },
            { label: "Wyzed available", value: stats.wyzedAvailable, icon: FiUsers },
          ].map(({ label, value, icon: Icon }) => (
            <div
              key={label}
              className="rounded-lg border border-gray-200 bg-white px-4 py-3"
            >
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                <Icon className="h-3.5 w-3.5" />
                {label}
              </div>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-b border-gray-100 bg-gray-50 px-5 py-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
              <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={vertexSearch}
                onChange={(e) => setVertexSearch(e.target.value)}
                placeholder="Search Vertex worker name or email…"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {vertexSearch && (
                <button
                  type="button"
                  onClick={() => setVertexSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
              <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={wyzedSearch}
                onChange={(e) => setWyzedSearch(e.target.value)}
                placeholder="Search mapped Wyzed learner…"
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {wyzedSearch && (
                <button
                  type="button"
                  onClick={() => setWyzedSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setFilter(tab.id)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  filter === tab.id
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 ring-1 ring-gray-300 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          Showing {filteredWorkers.length} of {workers.length} workers
          {stats.auto > 0 && ` · ${stats.auto} auto-matched`}
          {creatableWorkers.length > 0 &&
            ` · ${creatableWorkers.length} can be created in Wyzed`}
        </p>
      </div>

      {workers.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-gray-500">
          No active workers found for this organisation.
        </div>
      ) : filteredWorkers.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <p className="text-sm text-gray-500">No workers match your current filters.</p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="max-h-[min(70vh,720px)] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
              <tr className="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={allFilteredCreatableSelected}
                    disabled={creatableFilteredWorkers.length === 0 || bulkCreating}
                    onChange={toggleSelectAllFilteredCreatable}
                    aria-label="Select all creatable workers in view"
                  />
                </th>
                <th className="px-5 py-3 w-[30%]">Vertex360 Worker</th>
                <th className="px-5 py-3 w-[24%]">Email</th>
                <th className="px-5 py-3 w-[40%]">Wyzed Learner</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredWorkers.map((row) => {
                const canCreate = isCreatableOnWyzed(row);
                const isSelected = selectedCreateSet.has(String(row.employee_profile_id));
                const hasWyzedEmailMatch =
                  !row.wyzed_user_id &&
                  Boolean(row.suggested_wyzed_user_id);

                return (
                <tr
                  key={row.employee_profile_id}
                  className={`transition-colors hover:bg-gray-50/80 ${
                    !row.wyzed_user_id ? "bg-amber-50/40" : ""
                  } ${isSelected ? "bg-blue-50/40" : ""}`}
                >
                  <td className="px-3 py-3 align-top">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                      checked={isSelected}
                      disabled={!canCreate || bulkCreating}
                      onChange={() => toggleSelectWorker(row.employee_profile_id)}
                      aria-label={`Select ${row.worker_name}`}
                    />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600">
                        {(row.worker_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-900">
                          {row.worker_name}
                        </p>
                        {!row.wyzed_user_id && (
                          <p className="text-xs text-amber-700">
                            {hasWyzedEmailMatch
                              ? "Wyzed learner exists — match by email"
                              : !(row.worker_email || "").trim()
                                ? "Email required to create in Wyzed"
                                : "Needs mapping"}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-gray-600">
                    {row.worker_email || "—"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1">
                      <SearchableLearnerPicker
                        value={row.wyzed_user_id ?? ""}
                        options={getOptionsForRow(row)}
                        onChange={(id) =>
                          handleMappingChange(row.employee_profile_id, id)
                        }
                      />
                      {(() => {
                        const workerEmail = (row.worker_email || "").trim().toLowerCase();
                        const wyzedEmail = (row.wyzed_email || "").trim().toLowerCase();
                        const emailsMatch =
                          Boolean(row.wyzed_user_id) &&
                          workerEmail !== "" &&
                          wyzedEmail !== "" &&
                          workerEmail === wyzedEmail;

                        return (row.auto_matched || emailsMatch) && row.wyzed_user_id ? (
                          <span className="inline-flex w-fit items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            <FiCheck className="h-3 w-3" />
                            Auto-matched
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-3">
        <p className="text-xs text-gray-500">
          Tip: auto-match by email first, then create remaining workers in Wyzed, then save.
        </p>
        <CustomButton
          label="Save Mappings"
          showLoading={saving}
          isDisabled={saving || bulkCreating}
          clickHandler={handleSave}
        />
      </div>

      <ModalSkeleton
        isOpen={bulkCreateSelectedOpen}
        cssClass="w-[90%] max-w-lg"
        title="Create selected workers in Wyzed?"
        closeModal={() => !bulkCreating && setBulkCreateSelectedOpen(false)}
        modalFooter={
          <>
            <CustomButton
              label="Cancel"
              variant="secondary"
              isDisabled={bulkCreating}
              clickHandler={() => setBulkCreateSelectedOpen(false)}
            />
            <CustomButton
              label="Yes, create in Wyzed"
              showLoading={bulkCreating}
              isDisabled={bulkCreating || selectedCreatableWorkers.length === 0}
              clickHandler={handleBulkCreateSelected}
            />
          </>
        }
      >
        <BulkCreateConfirmBody
          count={selectedCreatableWorkers.length}
          workersPreview={selectedCreatableWorkers}
        />
      </ModalSkeleton>

      <ModalSkeleton
        isOpen={bulkCreateAllOpen}
        cssClass="w-[90%] max-w-lg"
        title="Create all missing workers in Wyzed?"
        closeModal={() => !bulkCreating && setBulkCreateAllOpen(false)}
        modalFooter={
          <>
            <CustomButton
              label="Cancel"
              variant="secondary"
              isDisabled={bulkCreating}
              clickHandler={() => setBulkCreateAllOpen(false)}
            />
            <CustomButton
              label="Yes, create in Wyzed"
              showLoading={bulkCreating}
              isDisabled={bulkCreating || creatableWorkers.length === 0}
              clickHandler={handleBulkCreateAll}
            />
          </>
        }
      >
        <BulkCreateConfirmBody
          count={creatableWorkers.length}
          workersPreview={creatableWorkers}
        />
      </ModalSkeleton>
    </div>
  );
};

export default WyzedWorkerMapping;
